import React, { useState, useEffect, useRef } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { getClientNotes, saveClientNotes } from './coachApi';

export default function CoachNotesTab({ coachId, clients, lang }) {
  const nl = lang === 'nl';
  const [selectedClient, setSelectedClient] = useState(null);
  const [notes, setNotes]     = useState('');
  const [saved, setSaved]     = useState(true);
  const [saving, setSaving]   = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const saveTimer = useRef(null);

  useEffect(() => {
    if (!selectedClient) return;
    getClientNotes(coachId, selectedClient.user_id)
      .then(d => { setNotes(d.notes || ''); setSaved(true); })
      .catch(() => {});
  }, [selectedClient, coachId]);

  const handleChange = (val) => {
    setNotes(val);
    setSaved(false);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => doSave(val), 1500);
  };

  const doSave = async (val) => {
    if (!selectedClient) return;
    setSaving(true);
    await saveClientNotes(coachId, selectedClient.user_id, { notes: val }).catch(() => {});
    setSaving(false);
    setSaved(true);
    setLastSaved(new Date().toLocaleTimeString());
  };

  const wordCount = notes.trim() ? notes.trim().split(/\s+/).length : 0;

  return (
    <div style={{ padding: '0 0 32px' }}>
      <style>{CSS}</style>
      <div style={{ color: C.white, fontSize: 17, fontWeight: 700, marginBottom: 4 }}>{nl ? 'Klantnotities' : 'Client Notes'}</div>
      <div style={{ color: C.silver, fontSize: 12, marginBottom: 16 }}>{nl ? 'Privé — alleen jij ziet deze notities' : 'Private — only you can see these notes'}</div>

      {/* Client selector */}
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, marginBottom: 16 }}>
        {(clients || []).length === 0 ? (
          <div style={{ color: C.silver2, fontSize: 12 }}>{nl ? 'Nog geen klanten' : 'No clients yet'}</div>
        ) : (
          (clients || []).map(c => {
            const active = selectedClient?.user_id === c.user_id;
            return (
              <button key={c.user_id} onClick={() => setSelectedClient(c)}
                style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 20, border: `1.5px solid ${active ? _T.accent : _T.cardBorderBot}`, background: active ? `${_T.accent}20` : _T.bg4, cursor: 'pointer' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: `${_T.accent}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                  {c.avatar || '👤'}
                </div>
                <span style={{ color: active ? _T.accent : C.silver, fontSize: 12, fontWeight: active ? 700 : 400 }}>{c.name}</span>
              </button>
            );
          })
        )}
      </div>

      {!selectedClient ? (
        <div style={{ textAlign: 'center', color: C.silver, padding: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>📝</div>
          <div style={{ fontSize: 14 }}>{nl ? 'Selecteer een klant om notities te bekijken' : 'Select a client to view notes'}</div>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ color: C.white, fontSize: 14, fontWeight: 600 }}>
              {selectedClient.name}
            </div>
            <div style={{ fontSize: 11, color: saved ? '#4AC87A' : C.silver }}>
              {saving ? (nl ? 'Opslaan...' : 'Saving...') : saved ? (lastSaved ? `✓ ${nl ? 'Opgeslagen' : 'Saved'} ${lastSaved}` : (nl ? '✓ Opgeslagen' : '✓ Saved')) : (nl ? 'Niet opgeslagen' : 'Unsaved changes')}
            </div>
          </div>

          <textarea
            value={notes}
            onChange={e => handleChange(e.target.value)}
            placeholder={nl
              ? `Notities over ${selectedClient.name}...\n\nBijv: doelen, besproken onderwerpen, voortgang, aandachtspunten, persoonlijke context...`
              : `Notes about ${selectedClient.name}...\n\nE.g: goals, discussed topics, progress, focus areas, personal context...`}
            style={{
              width: '100%', minHeight: 320, background: _T.bg3, border: `1px solid ${saved ? _T.cardBorderBot : `${_T.accent}55`}`,
              borderRadius: 14, color: C.white, fontSize: 13, padding: '16px', resize: 'vertical',
              boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.7, outline: 'none',
              transition: 'border-color 0.2s',
            }}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <div style={{ fontSize: 11, color: C.silver2 }}>
              {wordCount} {nl ? 'woorden' : 'words'}
            </div>
            <button onClick={() => doSave(notes)} disabled={saved || saving}
              style={{ background: !saved ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : 'none', border: `1px solid ${!saved ? 'transparent' : _T.cardBorderBot}`, borderRadius: 10, color: !saved ? '#0a1220' : C.silver2, fontSize: 12, fontWeight: 700, padding: '7px 16px', cursor: !saved ? 'pointer' : 'default' }}>
              {nl ? 'Opslaan' : 'Save'}
            </button>
          </div>

          {/* Quick tags */}
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 10, color: C.silver2, letterSpacing: 2, marginBottom: 8 }}>{nl ? 'SNELLE LABELS' : 'QUICK TAGS'}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {[
                { tag: '#doel', en: '#goal' }, { tag: '#zorg', en: '#concern' },
                { tag: '#progressie', en: '#progress' }, { tag: '#actie', en: '#action' },
                { tag: '#herhalen', en: '#followup' }, { tag: '#doorbraak', en: '#breakthrough' },
              ].map(t => (
                <button key={t.tag} onClick={() => handleChange(notes + (notes.endsWith(' ') || !notes ? '' : ' ') + (nl ? t.tag : t.en) + ' ')}
                  style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 20, color: C.silver, fontSize: 11, padding: '4px 10px', cursor: 'pointer' }}>
                  {nl ? t.tag : t.en}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
